import React from 'react';
import {
  ChevronRight,
  ArrowUpDown,
  Calendar,
  User,
  Mail,
  Tag,
  Activity
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { cn } from '../../lib/utils';

const UsersTable = ({
  users,
  selectedUsers,
  onSelectUser,
  onSelectAll,
  onUserClick,
  sortConfig,
  onSort,
  itemsPerPage,
  currentPage,
  totalUsers
}) => {
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUsers = users.slice(startIndex, endIndex);

  const getJourneyStageColor = (stage) => {
    switch (stage) {
      case 'paid':
        return 'bg-emerald-500/10 text-emerald-500';
      case 'saved':
        return 'bg-purple-500/10 text-purple-500';
      case 'started':
        return 'bg-yellow-500/10 text-yellow-500';
      case 'engaged':
        return 'bg-orange-500/10 text-orange-500';
      case 'new':
        return 'bg-gray-500/10 text-gray-500';
      default:
        return 'bg-red-500/10 text-red-500';
    }
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'Unknown';

    const now = new Date();
    const activityDate = timestamp?.toDate?.() || new Date(timestamp);
    const diffInMs = now - activityDate;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays}d ago`;
    } else if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7);
      return `${weeks}w ago`;
    } else if (diffInDays < 365) {
      const months = Math.floor(diffInDays / 30);
      return `${months}mo ago`;
    } else {
      const years = Math.floor(diffInDays / 365);
      return `${years}y ago`;
    }
  };

  // Mobile user card component
  const MobileUserCard = ({ user }) => (
    <div
      className="p-4 border border-border rounded-lg bg-card hover:bg-muted/50 cursor-pointer transition-colors"
      onClick={() => onUserClick(user)}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <Checkbox
            checked={selectedUsers.includes(user.id)}
            onCheckedChange={(checked) => onSelectUser(user.id, checked)}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Select ${user.displayName || user.name || 'Unknown User'}`}
          />
          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
            <User className="h-5 w-5 text-muted-foreground" />
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 text-foreground hover:bg-muted"
          onClick={(e) => {
            e.stopPropagation();
            onUserClick(user);
          }}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-2">
        <div>
          <h3 className="font-medium text-foreground">{user.displayName || user.name || 'Unknown User'}</h3>
          <p className="text-sm text-muted-foreground flex items-center gap-1">
            <Mail className="h-3 w-3" />
            {user.email}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Badge className={getJourneyStageColor(user.journeyStage)}>
            {user.journeyStage}
          </Badge>
          <div className={cn(
            "text-sm font-medium",
            user.valueScore >= 80 ? "text-emerald-500" :
              user.valueScore >= 60 ? "text-yellow-500" :
                "text-red-500"
          )}>
            Score: {user.valueScore}
          </div>
        </div>

        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <Activity className="h-3 w-3" />
          {formatRelativeTime(user.lastActivity || user.lastActivityTimestamp || user.createdAt)}
        </div>

        {user.tags && user.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {user.tags.slice(0, 3).map((tag, index) => (
              <Badge key={index} variant="outline" className="text-xs">
                {tag}
              </Badge>
            ))}
            {user.tags.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{user.tags.length - 3}
              </Badge>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div>
      {/* Desktop Table */}
      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-muted/50">
              <TableHead className="w-[40px]">
                <Checkbox
                  checked={selectedUsers.length === currentUsers.length && currentUsers.length > 0}
                  onCheckedChange={onSelectAll}
                  aria-label="Select all"
                  className="translate-y-[2px]"
                />
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  className="h-8 -ml-4 gap-2 text-foreground hover:bg-muted"
                  onClick={() => onSort('name')}
                >
                  Name
                  <ArrowUpDown className="h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead className="text-foreground">Email</TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  className="h-8 -ml-4 gap-2 text-foreground hover:bg-muted"
                  onClick={() => onSort('journeyStage')}
                >
                  Journey Stage
                  <ArrowUpDown className="h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  className="h-8 -ml-4 gap-2 text-foreground hover:bg-muted"
                  onClick={() => onSort('valueScore')}
                >
                  Value Score
                  <ArrowUpDown className="h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  className="h-8 -ml-4 gap-2 text-foreground hover:bg-muted"
                  onClick={() => onSort('lastActivity')}
                >
                  Last Activity
                  <ArrowUpDown className="h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead className="w-[40px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentUsers.map((user) => (
              <TableRow
                key={user.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => onUserClick(user)}
              >
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedUsers.includes(user.id)}
                    onCheckedChange={(checked) => onSelectUser(user.id, checked)}
                    aria-label={`Select ${user.displayName || user.name || 'Unknown User'}`}
                    className="translate-y-[2px]"
                  />
                </TableCell>
                <TableCell className="font-medium text-foreground">{user.displayName || user.name || 'Unknown User'}</TableCell>
                <TableCell className="text-muted-foreground">{user.email}</TableCell>
                <TableCell>
                  <Badge className={getJourneyStageColor(user.journeyStage)}>
                    {user.journeyStage}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className={cn(
                    "font-medium",
                    user.valueScore >= 80 ? "text-emerald-500" :
                      user.valueScore >= 60 ? "text-yellow-500" :
                        "text-red-500"
                  )}>
                    {user.valueScore}
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatRelativeTime(user.lastActivity || user.lastActivityTimestamp || user.createdAt)}
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-foreground hover:bg-muted"
                    onClick={(e) => {
                      e.stopPropagation();
                      onUserClick(user);
                    }}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-3">
        {currentUsers.map((user) => (
          <MobileUserCard key={user.id} user={user} />
        ))}
      </div>
    </div>
  );
};

export default UsersTable; 